import React from "react";
import { FiTrash2, FiX, FiZap } from "react-icons/fi";
import GatedButton from "../GatedButton";
import { LoadingSpinner } from "../LoadingSpinner";

const WRITE_TOKEN = "feature:storage:write";

export interface BulkBarProps {
	/** Nombre de fichiers sélectionnés : la barre ne se rend pas à 0. */
	count: number;
	/** Sous-ensemble d'images (seules les images se recompressent). */
	imageCount: number;
	/** Une action bulk est en cours : boutons verrouillés. */
	busy: boolean;
	onRecompress: () => void;
	onDelete: () => void;
	onClear: () => void;
}

/**
 * Barre d'actions sur la sélection multiple, flottante en bas de la zone de contenu :
 * recompression des images sélectionnées (POST /{id}/recompress en série), suppression
 * (confirmée par le parent) et désélection. Toutes les écritures passent par GatedButton
 * feature:storage:write.
 */
export const BulkBar: React.FC<BulkBarProps> = ({ count, imageCount, busy, onRecompress, onDelete, onClear }) => {
	if (count <= 0) return null;
	return (
		<div className="pointer-events-none sticky bottom-4 z-40 flex justify-center" role="region" aria-label="Actions sur la sélection">
			<div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-lg">
				<span className="text-sm font-semibold text-slate-800">
					{count.toLocaleString("fr-FR")} fichier{count > 1 ? "s" : ""} sélectionné{count > 1 ? "s" : ""}
				</span>
				<span className="h-4 w-px bg-slate-200" aria-hidden />
				<GatedButton
					token={WRITE_TOKEN}
					onClick={onRecompress}
					disabled={busy || imageCount === 0}
					title={imageCount === 0 ? "Aucune image dans la sélection" : "Régénère la copie compressée des images sélectionnées"}
					className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3.5 py-1.5 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{busy ? <LoadingSpinner size="xs" inline /> : <FiZap className="h-3.5 w-3.5" />} Recompresser les images ({imageCount})
				</GatedButton>
				<GatedButton
					token={WRITE_TOKEN}
					onClick={onDelete}
					disabled={busy}
					className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3.5 py-1.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiTrash2 className="h-3.5 w-3.5" /> Supprimer la sélection
				</GatedButton>
				<button
					type="button"
					onClick={onClear}
					disabled={busy}
					className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiX className="h-3.5 w-3.5" /> Tout désélectionner
				</button>
			</div>
		</div>
	);
};
